import React from 'react';

import orderBy from 'lodash.orderby';
import type { FieldValues } from 'react-hook-form';

import type { QuickAddSectionProps, QuickItemRenderer } from '@/_containers';
import { SyncQuickAddSection } from '@/_containers';
import { nameResolver } from '@/_shared';

import { Item } from './item';

import type { ContactQueryLinkerSchema } from '../../schemas';

type ContactSyncMultiSelectProps<TFormValues extends FieldValues = FieldValues> = Pick<
  QuickAddSectionProps<ContactQueryLinkerSchema, TFormValues>,
  'isEditable' | 'control' | 'fieldName' | 'title' | 'sectionTitle' | 'icon' | 'rightSlot'
> & {
  items: ContactQueryLinkerSchema[];
  options: ContactQueryLinkerSchema[];
  showHeader?: boolean;
  sortable?: boolean; // Sortable by drag n drop
};

export function SyncContactMultiSelect<TFormValues extends FieldValues = FieldValues>({
  title = 'Contacts',
  sectionTitle = 'Contacts',
  items = [],
  sortable = false,
  ...rest
}: ContactSyncMultiSelectProps<TFormValues>) {
  const renderItem: QuickItemRenderer<ContactQueryLinkerSchema> = React.useCallback(
    (item, { isEditable, removeItem }) => (
      <Item key={item.id} item={item} isEditable={isEditable} sortable={sortable} removeItemHandler={removeItem} />
    ),
    [sortable]
  );

  const itemToString = React.useCallback(
    (item: ContactQueryLinkerSchema | null) => (item ? nameResolver(item.first_name, item.last_name) : ''),
    []
  );

  const sortValues = React.useCallback(
    // TO SEE THE DIFFERENCE, CHANGE 'name' TO 'itemToString'
    (values: ContactQueryLinkerSchema[]) => orderBy(values, itemToString),
    [itemToString]
  );

  const keyExtractor = React.useCallback((item: ContactQueryLinkerSchema) => item.id.toString(), []);

  const filterOptions = React.useCallback(
    (item: ContactQueryLinkerSchema) => items.some((i) => i.id === item.id),
    [items]
  );

  const sortedItems = React.useMemo(() => (sortable ? items : sortValues(items)), [items, sortValues, sortable]);

  return (
    <SyncQuickAddSection
      {...rest}
      sectionTitle={sectionTitle}
      title={title}
      emptyDataMessage="No Contacts"
      items={sortedItems}
      keyExtractor={keyExtractor}
      itemToString={itemToString}
      renderItem={renderItem}
      sortValues={sortable ? undefined : sortValues}
      filterOptions={filterOptions}
      sortable={sortable}
    />
  );
}
